import * as React from 'react'

import {
  type AlignItem,
  type BordersStyles,
  Box,
  type Colors,
  EnhancedDimension,
  type JustifyContent,
  Text,
} from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import TooltipWrapper from 'modules/shift_management/components/ShiftManagement/TooltipWrapper'
import { useCurrentViewOptions } from 'modules/shift_management/hooks/useCurrentViewOptions'
import { useSchedule } from 'modules/shift_management/hooks/useSchedule'
import type View from 'modules/shift_management/store/internal/models/View'

interface ContentProps {
  children: React.ReactNode
  view: View
  background?: Colors
  border?: BordersStyles
  flexGrow?: boolean
  alignItems?: AlignItem
  justifyContent?: JustifyContent
  className?: string
  tooltip?: React.ReactNode
  body?: React.ReactNode
  span?: number
}

const Content = ({
  children,
  view,
  background,
  border,
  flexGrow = true,
  alignItems = 'center',
  justifyContent = 'center',
  className,
  tooltip,
  body,
  span = 1,
}: ContentProps) => {
  return (
    <Box
      // Can't use maxWidth prop directly because gamma does not have s170 as a dimension size
      // which is necessary to accommodate the new cell design
      className={className}
      flexGrow={flexGrow}
      justifyContent={justifyContent}
      alignItems={alignItems}
      borderRadius={{ all: 'abs008' }}
      border={border}
      background={background}
      paddingX="s8"
      paddingY="s4"
      flexNoShrink
    >
      {tooltip ? (
        <TooltipWrapper.ItemList label={tooltip}>
          <Box
            width={!view.isFullVersion ? 's40' : undefined}
            // Use maxWidth with spans to have overflow and ellipsis.
            // Default span is 1, so maxWidth is 120px.
            // For leaves the span would be N days, so maxWidth would be N * 120px.
            style={{
              maxWidth: view.isWeekly ? `${span * 120}px` : undefined,
            }}
          >
            <Text.Normal size="100" weight="bold" color="grey1000" ellipsis>
              {children}
            </Text.Normal>
          </Box>
        </TooltipWrapper.ItemList>
      ) : (
        <Text.Normal size="100" weight="bold" color="grey1000" ellipsis>
          {children}
        </Text.Normal>
      )}

      {view.isFullVersion && (
        <Text.Normal size="100" weight="normal" ellipsis>
          {body}
        </Text.Normal>
      )}
    </Box>
  )
}

interface Props {
  children: React.ReactNode
  background?: Colors
  border?: BordersStyles
  tooltip?: React.ReactNode
  maxHeight?: EnhancedDimension
  minHeight?: EnhancedDimension
  flexGrow?: boolean
  alignItems?: AlignItem
  justifyContent?: JustifyContent
  className?: string
  body?: React.ReactNode
  span?: number
}
const BaseCell = ({ children, ...props }: Props) => {
  const [{ view }] = useSchedule()
  const { viewOptions } = useCurrentViewOptions()
  const {
    schedule: { showCompactView },
  } = viewOptions

  const defaultMaxHeight = showCompactView ? view.cellMinHeight : view.cellMaxHeight
  const defaultMinHeight = view.cellMinHeight

  return (
    <Box
      flexGrow
      height={view.cellMaxHeight}
      maxHeight={props.maxHeight || defaultMaxHeight}
      minHeight={props.minHeight || defaultMinHeight}
      cursor="pointer"
    >
      <Content view={view} {...props}>
        {children}
      </Content>
    </Box>
  )
}

export default observer(BaseCell)
